/**
 * Case studies shown on /case-studies and featured on the homepage.
 * Content is generalized for confidentiality. Do not add unverified metrics.
 */
export const caseStudies = [
  {
    id: "assembly-pickup-program",
    plant: "Ford Dearborn Truck Plant",
    logo: "/images/dearborn-truck.webp",
    logoWidth: 676,
    logoHeight: 260,
    title: "Phased Commissioning",
    summary: "",
    challenge:
      "The plant needed automated material movement on a live production line, delivered through a system that could expand in phases. The initial routes had to be commissioned without interrupting production.",
    solution:
      "The ASE team planned each phase, from the AMR routes and drop points to the line-side equipment required at every station. The ATI team worked alongside the fleet vendor and plant IT to connect the fleet to the plant's part-ordering system. The AMS team scheduled commissioning outside production hours, verified each station before go-live, and documented every fault by cause.",
    technology: "AMR fleet for recurring material movement and line-side tote/cart exchange.",
    result:
      "The fleet now operates in live production, and each expansion follows the same documented implementation process.",
    objectives: [
      "Automate recurring material movement to stabilize line supply.",
      "Standardize routes, drop-zones, and call/dispatch logic.",
      "Minimize aisle congestion and ad-hoc fork runs during peaks.",
    ],
    approach: [
      "On-site flow study and route catalog; definition of AMR missions and dwell rules.",
      "Drop-zone and handoff standards for line-side kitting and tote/cart exchange.",
      "Commissioning playbooks and acceptance criteria to scale additional routes.",
    ],
    highlights: [
      "22 AMRs launched with phased route enablement.",
      "Documented procedures for support and change control.",
      "Preparing larger second- and third-phase expansion.",
    ],
  },
  {
    id: "engine-plant-kitting",
    plant: "Ford Dearborn Engine Plant",
    logo: "/ford2.png",
    logoWidth: 3840,
    logoHeight: 2160,
    title: "Changeover Commissioning",
    summary: "",
    challenge:
      "Both the replenishment and changeover workflows had to be proven before the line could depend on them. Startup could not be the first time either workflow was tested.",
    solution:
      "The ASE team defined the paired-vehicle workflow and the handoff between the marketplace and the line. The ATI team connected the fleet manager to the plant's part-ordering system and designed the delivery flow around operator scan-in at the line, keeping every delivery traceable. The AMS team then commissioned the full sequence alongside the operators and drivers on each shift.",
    technology: "AMRs with cart interfaces, line-side handoff points, and order-generation integration.",
    result:
      "Replenishment was commissioned first on a tested path, and changeover will follow the same commissioning sequence.",
    objectives: [
      "Reduce line-side footprint with dynamic kitting positions.",
      "Automate cart movement and position updates at the line.",
      "Ensure reliable order generation and replenishment triggers.",
    ],
    approach: [
      "Designed kitting layout and dynamic position logic tied to line conditions.",
      "Specified AMR/cart interfaces and line-side handoff points.",
      "Integration plan for automatic order generation and verification.",
    ],
    highlights: [
      "Dynamic kitting concept integrated with AMR movement.",
      "Clear interface specs to support future scale-out.",
      "Factory test/commissioning checklist for repeatability.",
    ],
  },
  {
    id: "stamping-multiarea-plan",
    plant: "Ford Dearborn Stamping Plant",
    logo: "/images/ford-stamping.webp",
    logoWidth: 474,
    logoHeight: 474,
    title: "Multi-Area Commissioning",
    summary: "",
    challenge:
      "Multiple areas of the plant required a single commissioning plan, and production could not stop for a first run in each area.",
    solution:
      "The ASE team mapped the material flows and staged a catalog of routes for each area. The ATI team established the traffic rules and the charging plan. The AMS team developed the readiness checklist that each area must pass before it is enabled.",
    technology: "Plant-level AMR roadmap covering routes, staging, safety concept, and charging strategy.",
    result:
      "The plant received one commissioning plan for every area, with routes, traffic rules, a charging plan, and a readiness checklist.",
    objectives: [
      "Create a plant-wide roadmap for AMR adoption across sub-assemblies.",
      "Align safety concepts, traffic rules, and battery/charging strategy.",
      "Sequence rollout to minimize production risk and disruption.",
    ],
    approach: [
      "Current-state mapping of flows; takt, queue, and aisle studies.",
      "Preliminary route catalog, staging strategy, and charging plan.",
      "Phased rollout with budgetary model and decision gates.",
    ],
    highlights: [
      "Plant-level plan covering routes, staging, and safety concept.",
      "Readiness checklist for each area/cell prior to enablement.",
      "Coordination framework for OEM/vendor activities.",
    ],
  },
  {
    id: "transmission-multiphasic",
    plant: "Ford Livonia Automatic Transmission Plant",
    logo: "/images/ford-transmission.webp",
    logoWidth: 474,
    logoHeight: 474,
    title: "Multi-Fleet Commissioning",
    summary: "",
    challenge:
      "Two separate fleets had to share aisles and intersections, and every shared point needed to be tested before production began.",
    solution:
      "The ASE team mapped the shared routes, stop points, and intersections. The ATI team coordinated both fleets through a single fleet manager using the open VDA 5050 protocol. The AMS team validated each intersection and trained the plant's operators and market managers.",
    technology: "AMRs with standardized call points, drop zones, and permissions across phases.",
    result:
      "Phase 1 entered production in early 2026, and Phase 2 planning follows the same commissioning method.",
    objectives: [
      "Introduce AMRs with low-risk pilot scope, then scale by area.",
      "Standardize interfaces (call points, drop zones, permissions).",
      "Establish operational run rules and change management.",
    ],
    approach: [
      "Pilot cell commissioning and lessons-learned loop.",
      "Template route definitions for repeat use in new areas.",
      "Operational playbooks for shift support and escalation.",
    ],
    highlights: [
      "Multi-phase plan with copy-and-scale route templates.",
      "Consistent acceptance criteria across phases.",
      "Centralized documentation for support and maintenance.",
    ],
  },
];

export const featuredCaseStudyIds = [
  "assembly-pickup-program",
  "engine-plant-kitting",
  "transmission-multiphasic",
];

export const featuredCaseStudies = featuredCaseStudyIds
  .map((id) => caseStudies.find((study) => study.id === id))
  .filter(Boolean);
